import React from 'react';
import COLORS from 'constants/colors';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Row, Col, Text, Separator } from '@cuemath/comps';
import { Circle } from 'styled-icons/boxicons-regular/Circle';
import { CommentDiscussion } from 'styled-icons/octicons/CommentDiscussion';

const SCORE = {
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

const CircleIcon = styled(Circle)`
  margin-top: 8px;
  color: ${COLORS.COLA_LIGHT};
`;

const StyledCol = styled(Col)`
  max-width: 5% !important;
`;

const StyledIcon = styled(CommentDiscussion)`
  color: ${COLORS.COLA};
  margin-right: 12px;
`;

const TextStyle = styled(Text)`
  margin-top: 5px;
  ol,
  ul {
    list-style: inherit !important;
  }
`;

const LinkNav = styled.a`
  cursor: pointer;
  display: block;
  color: ${COLORS.SKY};
  margin: 8px;
  font-size: 14px;
  font-weight: 600;
  &:hover {
    color: ${COLORS.SKY};
  }
  &:active {
    color: ${COLORS.SKY};
  }
`;

const Points = ({ children }) => (
  <Row gap={8}>
    <StyledCol sm={1} xs={1} gutter={0}>
      <CircleIcon size={14} />
    </StyledCol>
    <Col sm={11} xs={11} gutter={0}>
      {children}
    </Col>
  </Row>
);

Points.propTypes = {
  children: PropTypes.element,
};

Points.defaultProps = {
  children: null,
};

const PointsToDiscuss = ({
  attemptAnalysis,
  demoType,
  grade,
  salesConfig,
  teacherFeedback,
  salesPitch,
}) => {
  const { accuracy = 0 } = attemptAnalysis || {};
  const { links = [], proficiency_content: proficiencyConfig = [] } = salesConfig || {};
  const { answers = [] } = teacherFeedback || {};

  const totalScore = answers.length * SCORE.HIGH;
  let proficiencyScore = 0;

  answers.forEach(({ value }) => {
    proficiencyScore += SCORE[value.toUpperCase()];
  });

  const netProficiencyScore = totalScore ? (proficiencyScore / totalScore) * 100 : 0;

  const netProficiency = teacherFeedback ? netProficiencyScore : accuracy;

  const [proficiency] =
    proficiencyConfig && Array.isArray(proficiencyConfig) && proficiencyConfig.length > 0
      ? proficiencyConfig.filter(({ range }) => {
          const [greaterThanEqual, lessThanEqual] = range || [];
          return (
            (greaterThanEqual &&
              lessThanEqual &&
              netProficiency >= parseInt(greaterThanEqual) &&
              netProficiency <= parseInt(lessThanEqual)) ||
            (greaterThanEqual &&
              !lessThanEqual &&
              netProficiency >= parseInt(greaterThanEqual)) ||
            (lessThanEqual &&
              !greaterThanEqual &&
              netProficiency <= parseInt(lessThanEqual))
          );
        })
      : [];

  return (
    <Row>
      <Col sm={7} xs={12} gutter={0}>
        <Row alignItems="center" nowrap>
          <StyledIcon size={36} />
          <Text size="larger" color={COLORS.COLA}>
            Some points to discuss with Parent
          </Text>
        </Row>
        <Separator height={16} transparent />
        {(teacherFeedback || accuracy !== 0) && (
          <Points>
            <TextStyle
              color={COLORS.COLA}
              dangerouslySetInnerHTML={{
                __html: proficiency && proficiency.content,
              }}
            />
          </Points>
        )}
        {salesPitch && salesPitch.length > 0 && (
          <Points>
            <Text color={COLORS.COLA}>
              {' '}
              {salesPitch.map(text => {
                return (
                  <TextStyle
                    color={COLORS.COLA}
                    key={text}
                    dangerouslySetInnerHTML={{
                      __html: text,
                    }}
                  />
                );
              })}
            </Text>
          </Points>
        )}
      </Col>
      <Col smOffset={2} sm={3} xs={12}>
        <div>
          <Text color={COLORS.COLA} weight="bold">
            Important Links:
          </Text>
          <Separator height={4} transparent />
          {links.map(({ title, link }, idx) => (
            <LinkNav key={link} href={link} target="_blank">
              {idx + 1}. {title}
            </LinkNav>
          ))}
        </div>
      </Col>
    </Row>
  );
};

PointsToDiscuss.propTypes = {
  attemptAnalysis: PropTypes.shape({}),
  demoType: PropTypes.string,
  grade: PropTypes.string,
  salesPitch: PropTypes.string,
  salesConfig: PropTypes.shape({}),
  teacherFeedback: PropTypes.shape({}),
};

PointsToDiscuss.defaultProps = {
  attemptAnalysis: null,
  demoType: null,
  grade: null,
  salesPitch: null,
  salesConfig: null,
  teacherFeedback: null,
};

export default PointsToDiscuss;
